import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import type {
  CollectionRequest,
  CollectionStanding,
  ConnectedCollection,
  HistoryStanding,
} from '@/api/contract'
import type { Collected } from '~/api/client'
import { apiFetch } from '~/api/client'

export type { CollectionRequest, CollectionStanding, HistoryStanding }

const COLLECTIONS_KEY = ['collections'] as const

/** How often the standing is asked for while a collection is in the queue. */
const WHILE_QUEUED_MS = 3000

/**
 * And while a reconstruction is running, which is minutes per stretch rather
 * than seconds — so asking at the collection's rate would be a request every
 * three seconds for an answer that changes once a minute.
 */
const WHILE_BACKFILLING_MS = 30_000

/**
 * Whether this household is waiting to be collected, asked again while it is.
 *
 * Polled rather than pushed. A run reaches several providers and takes as long
 * as the slowest of them, so the answer the request comes back with is never the
 * final one — and a Durable Object holds no socket open for a page that may have
 * been closed halfway through a bank.
 */
export function useCollectionStanding(): UseQueryResult<CollectionStanding> {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: COLLECTIONS_KEY,
    queryFn: () => apiFetch<CollectionStanding>('/collections'),
    refetchInterval: (current) => {
      const standing = current.state.data
      if (standing?.queued === true) return WHILE_QUEUED_MS
      return (standing?.history?.length ?? 0) > 0 ? WHILE_BACKFILLING_MS : false
    },
  })

  /**
   * Every figure on every page is made of what the run just wrote, so the whole
   * cache goes when a run finishes. Without it the household watches a
   * collection finish and goes on being told there is no snapshot until
   * something else happens to refetch.
   *
   * Which run finished, not whether one is running. Watching `queued` meant
   * watching for a `true` that had to be *seen*, and a run that starts and
   * finishes between two polls is never seen — so the one case where the page
   * most needs refreshing was the one case that never refreshed it. A finish
   * time is still there to be compared afterwards, so nothing has to have been
   * looking at the right moment.
   */
  const seen = useRef<string | undefined>(undefined)
  const observed = useRef(false)
  const finishedAt = query.data?.finishedAt
  useEffect(() => {
    if (query.data === undefined) return
    // The first answer says which run had finished before this page opened, and
    // the figures on it are already that run's. Refetching them here would put
    // every mount of this hook through the whole cache for nothing.
    if (!observed.current) {
      observed.current = true
      seen.current = finishedAt
      return
    }
    if (finishedAt === undefined || finishedAt === seen.current) return
    seen.current = finishedAt
    void client.invalidateQueries()
  }, [query.data, finishedAt, client])

  return query
}

/**
 * The collection the last save started, and what the server said about it.
 *
 * Every save of a source now collects that source, so the sentence saying so
 * belongs on the page the save closed onto — and a save can come from any of a
 * dozen dialogs, most of which close the moment they succeed. Held in the query
 * cache rather than threaded through each of them, and answered from itself on
 * a refetch, so that the whole-cache refresh a finished collection triggers
 * does not wipe the sentence it was reporting on.
 */
const SAVED_COLLECTION_KEY = ['saved-collection'] as const

export function useSavedCollection(): ConnectedCollection | null {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: SAVED_COLLECTION_KEY,
    queryFn: () => client.getQueryData<ConnectedCollection | null>(SAVED_COLLECTION_KEY) ?? null,
    staleTime: Number.POSITIVE_INFINITY,
    initialData: null,
  })
  return query.data
}

/**
 * Records what a save started, and asks for the standing again so the page
 * starts watching the collection — which is what refreshes its figures when it
 * finishes, with nobody pressing the control.
 */
export function useRememberCollection(): (saved: unknown) => void {
  const client = useQueryClient()
  return (saved) => {
    const collection = (saved as Collected | null | undefined)?.collection
    if (collection === undefined) return
    client.setQueryData(SAVED_COLLECTION_KEY, collection)
    void client.invalidateQueries({ queryKey: COLLECTIONS_KEY })
  }
}

export function useRequestCollection(): UseMutationResult<CollectionRequest, Error, void> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<CollectionRequest>('/collections', { method: 'POST' }),
    // Only the standing: nothing has been collected yet, and a page that
    // refetched its figures here would suggest the run had already been.
    onSuccess: () => void client.invalidateQueries({ queryKey: COLLECTIONS_KEY }),
  })
}

/**
 * What the control says, and whether it can be pressed.
 *
 * The floor is named before anybody presses rather than only in the refusal: a
 * button that works some of the time and refuses the rest, with the rule said
 * only on refusal, reads as a button that is broken.
 */
export function collectionControl(
  standing: CollectionStanding | undefined,
  now: Date = new Date(),
): { label: string; note: string; disabled: boolean } {
  if (standing === undefined) {
    return { label: 'Collect now', note: '', disabled: true }
  }

  if (!standing.available) {
    return {
      label: 'Collect now',
      note: 'This dashboard collects on its nightly schedule only.',
      disabled: true,
    }
  }

  if (standing.queued) {
    return {
      label: 'Collecting…',
      note: join(queuedNote(standing), backfillNote(standing)),
      disabled: true,
    }
  }

  const waiting = minutesUntil(standing.nextAllowedAt, now)
  if (waiting !== undefined) {
    return {
      label: 'Collect now',
      // A source collected because it was just connected is what the floor is
      // most often counting from, and "asked for" would blame a press nobody
      // made. The control is the only thing here the floor applies to.
      note:
        standing.requestedSource === undefined
          ? `Asked for less than ${standing.floorMinutes} minutes ago. It can be asked for again in ${waiting}.`
          : `A source was collected less than ${standing.floorMinutes} minutes ago. Everything can be asked for again in ${waiting}.`,
      disabled: true,
    }
  }

  return {
    label: 'Collect now',
    note: join(
      `Reaches every connected source. Once every ${standing.floorMinutes} minutes, so a provider is not asked more often than it will stand.`,
      backfillNote(standing),
    ),
    disabled: false,
  }
}

/**
 * What a reconstruction still running has to say for itself.
 *
 * It is on the control rather than on the chart because the chart already says
 * it: a series drawn mid-backfill shades where its coverage is partial. What
 * nothing else says is that something is *still filling it in*, which is the
 * difference between a history that stops in 2024 and one that has not got
 * there yet.
 */
function backfillNote(standing: CollectionStanding): string | undefined {
  const filling = standing.history ?? []
  if (filling.length === 0) return undefined

  const sources = filling.map((one) => one.label).join(', ')
  // The day it has actually reached, never a promise about how far it will go:
  // how much past a provider will serve is only known once it stops.
  const reached = filling
    .map((one) => one.reached)
    .filter((day): day is string => day !== undefined)
    .sort()[0]

  return reached === undefined
    ? `Filling in the history behind ${sources}.`
    : `Filling in the history behind ${sources}, back to ${reached} so far.`
}

function join(...parts: (string | undefined)[]): string {
  return parts.filter((part): part is string => part !== undefined).join(' ')
}

/**
 * What is being collected, which is not always the household.
 *
 * A source collects itself the moment it is connected, so the queue this page
 * follows may hold one source rather than everything — and "reaching each
 * source in turn" would be a sentence about a run that is not happening.
 */
function queuedNote(standing: CollectionStanding): string {
  const fills = 'This page fills in as it finishes.'

  if (standing.ahead > 1) {
    const others = standing.ahead - 1
    return `Queued behind ${others} other ${others === 1 ? 'collection' : 'collections'}. ${fills}`
  }
  return (standing.sources ?? []).length > 0
    ? `Collecting the source you just connected. ${fills}`
    : `Reaching each source in turn. ${fills}`
}

/** Undefined once the moment has passed, which is what makes the control pressable. */
function minutesUntil(at: string | undefined, now: Date): string | undefined {
  if (at === undefined) return undefined
  const span = Date.parse(at) - now.getTime()
  if (Number.isNaN(span) || span <= 0) return undefined
  const whole = Math.max(1, Math.ceil(span / 60000))
  return `${whole} ${whole === 1 ? 'minute' : 'minutes'}`
}
